import { type ReactNode } from 'react';

import { AppBuilderIcon } from '@/icons/Icons';
import { ReactComponent as IconAbout } from '@/images/icons/icon-about.svg';
import { ReactComponent as IconChangelog } from '@/images/icons/icon-changelog.svg';
import { ReactComponent as IconCustomers } from '@/images/icons/icon-customers.svg';
import { ReactComponent as IconDocs } from '@/images/icons/icon-docs.svg';
import { ReactComponent as IconDownload } from '@/images/icons/icon-download.svg';
import { ReactComponent as IconEnterprise } from '@/images/icons/icon-enterprise.svg';
import { ReactComponent as IconExplore } from '@/images/icons/icon-explore.svg';
import { ReactComponent as IconForTeams } from '@/images/icons/icon-for-teams.svg';
import { ReactComponent as IconIntegrations } from '@/images/icons/icon-integrations.svg';
import { ReactComponent as IconLearn } from '@/images/icons/icon-learn.svg';
import { ReactComponent as IconMagic } from '@/images/icons/icon-magic.svg';
import { ReactComponent as IconNotebooks } from '@/images/icons/icon-notebooks.svg';
import { ReactComponent as IconBlog } from '@/images/icons/icon-our-blog.svg';
import { ReactComponent as IconPartners } from '@/images/icons/icon-partners.svg';
import { ReactComponent as IconPin } from '@/images/icons/icon-pin.svg';
import { ReactComponent as IconPricing } from '@/images/icons/icon-pricing.svg';
import { ReactComponent as IconProduct } from '@/images/icons/icon-product.svg';
import { ReactComponent as IconSemanticModels } from '@/images/icons/icon-semantic-models.svg';
import { ReactComponent as IconTemplates } from '@/images/icons/icon-templates.svg';
import { ReactComponent as IconDataScience } from '@/images/icons/icon-use-case-data-science.svg';
import { ReactComponent as IconExploratoryAnalysis } from '@/images/icons/icon-use-case-exploratory-analysis.svg';
import { ReactComponent as IconOperationalReporting } from '@/images/icons/icon-use-case-operational-reporting.svg';
import { ReactComponent as IconSelfServe } from '@/images/icons/icon-use-case-self-serve.svg';
import { ReactComponent as IconUseCases } from '@/images/icons/icon-use-cases.svg';
import { DOCS_LINKS, INTERNAL_LINKS } from '@/utils';

export type NavItem =
  | {
      dropdown: {
        lists: Array<{
          children: Array<{
            description: string;
            icon: ReactNode;
            title: string;
            to: string;
          }>;
          title: string;
        }>;
      };
      icon: ReactNode;
      title: string;
    }
  | {
      icon: ReactNode;
      title: string;
      to: string;
    };

export const leftNav: Array<NavItem> = [
  {
    title: 'Product',
    icon: <IconProduct />,
    dropdown: {
      lists: [
        {
          title: 'Products',
          children: [
            {
              icon: <IconNotebooks />,
              title: 'Notebooks',
              to: INTERNAL_LINKS.Notebooks,
              description: 'Analyze, model, and explore, like never before'
            },
            {
              icon: <IconExplore />,
              title: 'Explore',
              to: INTERNAL_LINKS.Explore,
              description:
                'Powerful exploration tools in a friendly, no-code UI'
            },
            {
              icon: <IconMagic />,
              title: 'Magic AI',
              to: INTERNAL_LINKS.Magic,
              description: 'Smarter, faster analysis'
            },
            {
              icon: <IconDownload />,
              title: 'Embedded analytics',
              to: INTERNAL_LINKS.EmbeddedAnalytics,
              description:
                'Seamlessly embed data experiences for your customers'
            },
            {
              icon: <IconSemanticModels />,
              title: 'Semantic models',
              to: INTERNAL_LINKS.SemanticModels,
              description: 'Sync governed metrics for consistent insights'
            },
            {
              icon: <IconForTeams />,
              title: 'Collaboration and sharing',
              to: INTERNAL_LINKS.ForTeams,
              description:
                'Bring everyone together in one collaborative workspace'
            },
            {
              icon: <AppBuilderIcon />,
              title: 'App Builder',
              to: INTERNAL_LINKS.AppBuilder,
              description:
                'Build and share insights with beautiful interactive visualizations'
            },
            {
              icon: <IconIntegrations />,
              title: 'Integrations',
              to: INTERNAL_LINKS.Integrations,
              description: 'Connect to anything'
            }
          ]
        }
      ]
    }
  },
  {
    title: 'Use cases',
    icon: <IconUseCases />,
    dropdown: {
      lists: [
        {
          title: 'Use cases',
          children: [
            {
              icon: <IconExploratoryAnalysis />,
              title: 'Exploratory analysis',
              to: INTERNAL_LINKS.UseCaseExploratoryAnalysis,
              description: 'The most powerful tools for exploration available.'
            },
            {
              icon: <IconDataScience />,
              title: 'Data science',
              to: INTERNAL_LINKS.UseCaseDataScience,
              description:
                'The first platform actually designed for data scientists.'
            },
            {
              icon: <IconOperationalReporting />,
              title: 'Operational reporting',
              to: INTERNAL_LINKS.UseCaseOperationalReporting,
              description:
                'Interactive, intuitive reporting without crazy workarounds.'
            },
            {
              icon: <IconSelfServe />,
              title: 'Self-serve',
              to: INTERNAL_LINKS.UseCaseSelfServe,
              description: 'Data tools for stakeholders and decision-makers.'
            }
          ]
        }
      ]
    }
  },
  {
    title: 'Enterprise',
    icon: <IconEnterprise />,
    to: INTERNAL_LINKS.Enterprise
  }
];

export const rightNav: Array<NavItem> = [
  {
    title: 'Resources',
    icon: <IconLearn />,
    dropdown: {
      lists: [
        {
          title: 'Get started',
          children: [
            {
              title: 'Templates',
              to: INTERNAL_LINKS.Templates,
              icon: <IconTemplates />,
              description: 'Jumpstart with pre-built projects'
            },
            {
              title: 'Hex Foundations',
              to: DOCS_LINKS.HexFoundations,
              icon: <IconAbout />,
              description: 'Video series'
            },
            {
              title: 'Docs',
              to: DOCS_LINKS.Home,
              icon: <IconDocs />,
              description: 'Resources and product guides'
            },
            {
              title: 'Changelog',
              to: DOCS_LINKS.Changelog,
              icon: <IconChangelog />,
              description: 'Product updates'
            }
          ]
        },
        {
          title: 'Inspiration',
          children: [
            {
              title: 'Blog',
              to: INTERNAL_LINKS.Blog,
              icon: <IconBlog />,
              description: 'From data teams to data teams'
            },
            {
              title: 'Resources & Events',
              to: INTERNAL_LINKS.Resources,
              icon: <IconPin />,
              description: 'Learn and connect with peers'
            },
            {
              title: 'Customer stories',
              to: INTERNAL_LINKS.Customers,
              icon: <IconCustomers />,
              description: 'Empowering the best data teams'
            },
            {
              title: 'Partners',
              to: INTERNAL_LINKS.Partners,
              icon: <IconPartners />,
              description: 'Learn more about our partnerships'
            }
          ]
        }
      ]
    }
  },
  {
    title: 'Pricing',
    icon: <IconPricing />,
    to: INTERNAL_LINKS.Pricing
  }
];
